<section class="account-section card shadow-sm">
  <header class="section-header">
    <span class="section-icon"><i class="pi pi-shield"></i></span>
    <div>
      <h2 class="section-title">{{ "mfa.section-title" | translate }}</h2>
      <p class="section-subtitle">{{ "mfa.section-subtitle" | translate }}</p>
    </div>
  </header>

  @if (statusError) {
    <div class="alert alert-warning mb-0">
      {{ "mfa.error-status" | translate }}
    </div>
  } @else {
    <!-- Painel principal do 2FA -->
    <div class="mfa-panel" [class.mfa-panel-on]="mfaEnabled === true">
      <div class="mfa-panel-main">
        <span class="mfa-panel-icon"><i class="pi pi-shield"></i></span>
        <div class="mfa-panel-info">
          <h3 class="mfa-panel-title">{{ "mfa.title" | translate }}</h3>
          <p class="mfa-panel-text">{{ "mfa.description" | translate }}</p>
          @if (mfaEnabled === false) {
            <p class="mfa-hint">
              <i class="pi pi-info-circle"></i>
              {{ "mfa.need-app" | translate }}
            </p>
          }
        </div>
      </div>

      <div class="mfa-panel-side">
        @if (mfaEnabled === null) {
          <span class="mfa-loading">{{ "mfa.loading" | translate }}</span>
        } @else if (mfaEnabled) {
          <p-tag
            styleClass="mfa-tag"
            severity="success"
            icon="pi pi-check-circle"
            [value]="'mfa.badge-on' | translate"
          />
          <p-button
            severity="danger"
            icon="pi pi-lock-open"
            [label]="'mfa.button-disable' | translate"
            (onClick)="openDisable()"
          />
        } @else {
          <p-tag
            styleClass="mfa-tag"
            severity="danger"
            icon="pi pi-times-circle"
            [value]="'mfa.badge-off' | translate"
          />
          <p-button
            severity="success"
            icon="pi pi-shield"
            [label]="'mfa.button-enable' | translate"
            (onClick)="openSetup()"
          />
        }
      </div>
    </div>

    <!-- Como funciona (só quando o 2FA está desativado) -->
    @if (mfaEnabled === false) {
      <div class="mfa-how">
        <h4 class="mfa-how-title">{{ "mfa.how-title" | translate }}</h4>
        <ol class="mfa-how-steps">
          <li>
            <span class="mfa-how-number">1</span>
            <span>{{ "mfa.how-step-app" | translate }}</span>
          </li>
          <li>
            <span class="mfa-how-number">2</span>
            <span>{{ "mfa.how-step-scan" | translate }}</span>
          </li>
          <li>
            <span class="mfa-how-number">3</span>
            <span>{{ "mfa.how-step-code" | translate }}</span>
          </li>
        </ol>
        <p class="mfa-how-after">
          <i class="pi pi-key"></i>
          {{ "mfa.how-after" | translate }}
        </p>
        <p class="mfa-how-note">{{ "mfa.enable-hint" | translate }}</p>
      </div>
    }

    <!-- Códigos de recuperação -->
    <div class="mfa-recovery">
      <div class="mfa-panel-main">
        <span class="mfa-recovery-icon"><i class="pi pi-file"></i></span>
        <div class="mfa-panel-info">
          <h4 class="mfa-recovery-title">{{ "mfa.codes-title" | translate }}</h4>
          <p class="mfa-recovery-text">
            {{
              (mfaEnabled ? "mfa.recovery-text-on" : "mfa.recovery-text-off")
                | translate
            }}
          </p>
        </div>
      </div>
      <p-button
        severity="secondary"
        [outlined]="true"
        icon="pi pi-refresh"
        [label]="'mfa.button-regenerate-short' | translate"
        [disabled]="mfaEnabled !== true"
        (onClick)="openRegenerate()"
      />
    </div>
  }
</section>

@if (dialog !== "none") {
  <div class="mfa-backdrop"></div>
  <div
    class="mfa-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="mfa-modal-title"
  >
    <div class="mfa-modal-content">
      @if (step === "codes") {
        <!-- Códigos de recuperação (ativação e regeneração) -->
        <h5 id="mfa-modal-title">{{ "mfa.codes-title" | translate }}</h5>
        <p class="mfa-warning">{{ "mfa.codes-warning" | translate }}</p>
        <ul class="mfa-codes">
          @for (recoveryCode of recoveryCodes; track recoveryCode) {
            <li>{{ recoveryCode }}</li>
          }
        </ul>
        <div class="mfa-codes-actions">
          <p-button
            severity="secondary"
            [outlined]="true"
            size="small"
            icon="pi pi-copy"
            [label]="(copied ? 'mfa.codes-copied' : 'mfa.codes-copy') | translate"
            (onClick)="copyCodes()"
          />
          <p-button
            severity="secondary"
            [outlined]="true"
            size="small"
            icon="pi pi-download"
            [label]="'mfa.codes-download' | translate"
            (onClick)="downloadCodes()"
          />
        </div>
        <label class="mfa-saved">
          <input
            type="checkbox"
            [checked]="codesSaved"
            (change)="codesSaved = $any($event.target).checked"
          />
          {{ "mfa.codes-saved" | translate }}
        </label>
        <div class="mfa-footer">
          <p-button
            severity="success"
            [label]="'mfa.button-done' | translate"
            [disabled]="!codesSaved"
            (onClick)="closeDialog()"
          />
        </div>
      } @else {
        @if (dialog === "setup") {
          <h5 id="mfa-modal-title">{{ "mfa.setup-title" | translate }}</h5>
          <ol class="mfa-instructions">
            <li>{{ "mfa.setup-step-app" | translate }}</li>
            <li>{{ "mfa.setup-step-scan" | translate }}</li>
            <li>{{ "mfa.setup-step-code" | translate }}</li>
          </ol>
          <div class="mfa-qr">
            @if (qrCode) {
              <img
                [src]="qrCode"
                [alt]="'mfa.qr-alt' | translate"
                width="220"
                height="220"
              />
            } @else if (loading) {
              <span>{{ "mfa.qr-loading" | translate }}</span>
            }
          </div>
        }

        @if (dialog === "regenerate") {
          <h5 id="mfa-modal-title">{{ "mfa.regenerate-title" | translate }}</h5>
          <p>{{ "mfa.regenerate-text" | translate }}</p>
        }

        @if (dialog === "disable") {
          <h5 id="mfa-modal-title">{{ "mfa.disable-title" | translate }}</h5>
          <p>{{ "mfa.disable-text" | translate }}</p>
          <label class="form-label" for="mfaPassword">{{
            "mfa.password-label" | translate
          }}</label>
          <input
            type="password"
            id="mfaPassword"
            class="form-control mb-3"
            [formControl]="passwordControl"
            autocomplete="current-password"
          />
        }

        <label class="form-label" for="mfaCode">{{
          "mfa.code-label" | translate
        }}</label>
        <p-inputotp
          class="mfa-otp"
          [formControl]="codeControl"
          [length]="6"
          [integerOnly]="true"
          size="large"
          (keydown.enter)="submit()"
        />

        @if (errorKey) {
          <div class="mfa-error" role="alert">{{ errorKey | translate }}</div>
        }

        <div class="mfa-footer">
          <p-button
            severity="secondary"
            [outlined]="true"
            [label]="'mfa.button-cancel' | translate"
            [disabled]="loading"
            (onClick)="closeDialog()"
          />
          <p-button
            [severity]="dialog === 'disable' ? 'danger' : 'success'"
            [label]="'mfa.button-confirm' | translate"
            [loading]="loading"
            [disabled]="!canSubmit"
            (onClick)="submit()"
          />
        </div>
      }
    </div>
  </div>
}
